import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatInputModule } from '@angular/material/input';
import { Subscription, take } from 'rxjs';
import { UserSettings, APIResponse, AppData, IBankAccount, ObjectUtils, VatExemptionType } from 'fakturnia-shared';
import { SettingsApiService } from 'shared/services/api/settings.api.service';
import { SettingsService } from 'shared/services/settings.service';
import { SnackbarService } from 'shared/services/snackbar.service';

/**
 * Ustawienie: "Automatyczne adnotacje dla nowych dokumentów".
 *
 * Master włącznik, który przy tworzeniu nowego dokumentu zaznacza domyślne
 * adnotacje i ustawia ich wartości:
 *  - domyślna procedura marży (z wybraną procedurą),
 *  - domyślny mechanizm podzielonej płatności (MPP) + auto-wybór konta VAT.
 */
@Component({
  standalone: true,
  selector: 'auto-annotations',
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatIconModule,
    MatSlideToggleModule,
    MatTooltipModule
  ],
  templateUrl: './auto-annotations.component.html',
  styleUrls: ['./auto-annotations.component.scss']
})
export class AutoAnnotationsComponent implements OnInit, OnDestroy {

  private _subscriptions: Subscription[] = [];

  marginProcedures = AppData.MarginProcedures;
  vatExemptionTypes = AppData.VatExemptionTypes;

  autoAnnotations = false;
  defaultMarginProcedureEnabled = false;
  defaultMarginProcedureId: number | null = null;
  defaultSplitPayment = false;

  // Zwolnienie z VAT
  defaultVatExemptionEnabled = false;
  selectedExemptionType: VatExemptionType | null = null;
  exemptionLegalBasis = '';
  showVatExemptionBase = false;

  bankAccounts: IBankAccount[] = [];

  isProcessing = false;

  constructor(
    private _settingsApiService: SettingsApiService,
    private _settingsService: SettingsService,
    private _snackbarService: SnackbarService
  ) {}

  ngOnInit(): void {
    this._subscriptions.push(
      this._settingsService.getUserSettings().subscribe({
        next: (userSettings: UserSettings) => {
          const autocomplete = (userSettings?.settings?.autocomplete as any) || {};
          this.autoAnnotations = autocomplete.autoAnnotations === true;
          this.defaultMarginProcedureEnabled = autocomplete.defaultMarginProcedureEnabled === true;
          this.defaultMarginProcedureId = autocomplete.defaultMarginProcedureId ?? null;
          this.defaultSplitPayment = autocomplete.defaultSplitPayment === true;
          this.defaultVatExemptionEnabled = autocomplete.defaultVatExemptionEnabled === true;
          this.selectedExemptionType = autocomplete.defaultVatExemptionType || null;
          this.exemptionLegalBasis = autocomplete.defaultVatExemptionLegalBasis || '';
          this.showVatExemptionBase = autocomplete.showVatExemptionBase === true;
          this.bankAccounts = (userSettings?.bankAccounts as IBankAccount[]) || [];
        }
      })
    );
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach(sub => sub.unsubscribe());
  }

  /** Konto bankowe oznaczone jako konto VAT (dla MPP) */
  get vatAccount(): IBankAccount | null {
    return this.bankAccounts.find(acc => (acc as any).isVATAccount === true) || null;
  }

  /** Brak skonfigurowanego konta VAT, a MPP jest włączone */
  get showMissingVatAccountWarning(): boolean {
    return this.defaultSplitPayment && !this.vatAccount;
  }

  /** Numer konta sformatowany w grupach po 4 cyfry */
  formatAccountNumber(accountNumber?: string): string {
    if (!accountNumber) return '';
    return accountNumber.replace(/\s+/g, '').replace(/(.{4})/g, '$1 ').trim();
  }

  onMasterToggle(enabled: boolean): void {
    this.autoAnnotations = enabled;
    this.saveChanges({ autoAnnotations: enabled });
  }

  onMarginProcedureToggle(enabled: boolean): void {
    this.defaultMarginProcedureEnabled = enabled;
    // Przy włączeniu ustaw pierwszą procedurę, jeśli żadnej nie wybrano
    if (enabled && !this.defaultMarginProcedureId) {
      this.defaultMarginProcedureId = this.marginProcedures[0]?.id ?? null;
    }
    this.saveChanges({
      defaultMarginProcedureEnabled: enabled,
      defaultMarginProcedureId: this.defaultMarginProcedureId
    });
  }

  onMarginProcedureChange(procedureId: number): void {
    this.defaultMarginProcedureId = procedureId;
    this.saveChanges({ defaultMarginProcedureId: procedureId });
  }

  onSplitPaymentToggle(enabled: boolean): void {
    this.defaultSplitPayment = enabled;
    this.saveChanges({ defaultSplitPayment: enabled });
  }

  // ── Zwolnienie z VAT ──────────────────────────────────────────

  get selectedTypePlaceholder(): string {
    const type = this.vatExemptionTypes.find(t => t.type === this.selectedExemptionType);
    return type?.placeholder || 'Wpisz podstawę prawną...';
  }

  onVatExemptionToggle(enabled: boolean): void {
    this.defaultVatExemptionEnabled = enabled;
    this.saveVatExemption();
  }

  onExemptionTypeChange(type: VatExemptionType | null): void {
    this.selectedExemptionType = type;
    if (!type) this.exemptionLegalBasis = '';
    this.saveVatExemption();
  }

  onLegalBasisBlur(): void {
    this.saveVatExemption();
  }

  onShowVatExemptionChange(show: boolean): void {
    this.showVatExemptionBase = show;
    this.saveVatExemption();
  }

  /** Zapis ustawień zwolnienia z VAT przez dedykowany endpoint vat-exemption-base */
  private saveVatExemption(): void {
    if (this.isProcessing) return;
    this.isProcessing = true;

    this._settingsApiService.updateVatExemptionSettings(
      this.selectedExemptionType,
      this.exemptionLegalBasis || null,
      this.showVatExemptionBase,
      this.defaultVatExemptionEnabled
    )
      .pipe(take(1))
      .subscribe({
        next: (response: APIResponse) => {
          if (ObjectUtils.getNestedValue(response, 'success')) {
            this._settingsService.patchAutocomplete({
              defaultVatExemptionEnabled: this.defaultVatExemptionEnabled,
              defaultVatExemptionType: this.selectedExemptionType,
              defaultVatExemptionLegalBasis: this.exemptionLegalBasis || null,
              showVatExemptionBase: this.showVatExemptionBase
            });
            this._snackbarService.showSnackBar('Zapisano zmiany', 'success', 4000);
          } else {
            this._snackbarService.showSnackBar('Nie udało się zapisać zmian', 'error', 4000);
          }
          this.isProcessing = false;
        },
        error: () => {
          this._snackbarService.showSnackBar('Wystąpił błąd podczas zapisywania', 'error', 4000);
          this.isProcessing = false;
        }
      });
  }

  private saveChanges(payload: {
    autoAnnotations?: boolean
    defaultMarginProcedureEnabled?: boolean
    defaultMarginProcedureId?: number | null
    defaultSplitPayment?: boolean
  }): void {
    if (this.isProcessing) return;
    this.isProcessing = true;

    this._settingsApiService.updateAutoAnnotationsSettings(payload)
      .pipe(take(1))
      .subscribe({
        next: (response: APIResponse) => {
          if (ObjectUtils.getNestedValue(response, 'success')) {
            // Optymistyczna aktualizacja cache — bez pełnego refetcha, żeby
            // pozostałe przełączniki nie "mrugały" po zapisie pojedynczego pola.
            this._settingsService.patchAutocomplete(payload);
            this._snackbarService.showSnackBar('Zapisano zmiany', 'success', 4000);
          } else {
            this._snackbarService.showSnackBar('Nie udało się zapisać zmian', 'error', 4000);
          }
          this.isProcessing = false;
        },
        error: () => {
          this._snackbarService.showSnackBar('Wystąpił błąd podczas zapisywania', 'error', 4000);
          this.isProcessing = false;
        }
      });
  }
}
